import { useMemo, useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import type { CustomerRequest } from '@/types/request';

function lookup(rows: CustomerRequest[], id: string | null) {
  return id ? rows.find((r) => r.id === id) ?? null : null;
}

export function ApproveModal({
  id,
  rows,
  onClose,
  onConfirm,
}: {
  id: string | null;
  rows: CustomerRequest[];
  onClose: () => void;
  onConfirm: (id: string, note?: string) => void;
}) {
  const [note, setNote] = useState('');
  const r = useMemo(() => lookup(rows, id), [rows, id]);
  useEffect(() => { if (id) setNote(''); }, [id]);

  return (
    <Modal
      open={!!id}
      onOpenChange={(o) => !o && onClose()}
      title={r ? `Approve request ${r.referenceNumber}?` : 'Approve request'}
      description={
        r ? `Approving this ${r.requestType.replace('_', ' ').toLowerCase()} request will bill the customer and notify them by push + SMS.` : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="success"
            onClick={() => id && onConfirm(id, note.trim() || undefined)}
          >
            Approve
          </Button>
        </>
      }
    >
      <label className="text-caption text-ink-muted font-medium mb-1.5 inline-block">
        Internal note (optional)
      </label>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Anything the rest of the team should know…"
        rows={3}
        className="w-full rounded-lg border border-line bg-white px-3 py-2 text-body text-ink outline-none placeholder:text-ink-hint focus:border-brand-blue focus:shadow-focus resize-none"
      />
    </Modal>
  );
}

export function RejectModal({
  id,
  rows,
  onClose,
  onConfirm,
}: {
  id: string | null;
  rows: CustomerRequest[];
  onClose: () => void;
  onConfirm: (id: string, reason: string) => void;
}) {
  const r = useMemo(() => lookup(rows, id), [rows, id]);
  const [reason, setReason] = useState('');
  useEffect(() => { if (id) setReason(''); }, [id]);

  const charCount = reason.length;
  const valid = reason.trim().length >= 8 && reason.length <= 500;

  return (
    <Modal
      open={!!id}
      onOpenChange={(o) => !o && onClose()}
      title={r ? `Reject request ${r.referenceNumber}?` : 'Reject request'}
      description={
        r
          ? 'The customer will receive a notification with the reason you provide. Be clear and respectful — this text is forwarded verbatim.'
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={!valid}
            onClick={() => id && valid && onConfirm(id, reason.trim())}
          >
            Reject
          </Button>
        </>
      }
    >
      <label className="text-caption text-ink-muted font-medium mb-1.5 inline-block">
        Reason for rejection <span className="text-status-error">*</span>
      </label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value.slice(0, 500))}
        placeholder="e.g. Outstanding balance on account exceeds threshold for this service. Please clear balance before re-submitting."
        rows={4}
        className="w-full rounded-lg border border-line bg-white px-3 py-2 text-body text-ink outline-none placeholder:text-ink-hint focus:border-brand-blue focus:shadow-focus resize-none"
      />
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-caption text-ink-muted">
          Minimum 8 characters.
        </span>
        <span className="text-caption text-ink-muted tabular">
          {charCount} / 500
        </span>
      </div>
    </Modal>
  );
}
